import axios, { AxiosError, AxiosResponse, AxiosProgressEvent } from 'axios';
import { Note, NoteCreate, NoteUpdate, NoteFilter } from './types/note';
import { Attachment } from './types/attachment';
import { LoginCredentials, UserCreate, AuthResponse, User } from './types/auth';
import { BACKEND_URL } from './config';
import { Category } from './types/category';

// Create an axios instance with default config
const api = axios.create({
  baseURL: BACKEND_URL, // FastAPI backend
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response error:', error.response.data);
      console.error('Status:', error.response.status);
      
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request error:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  // Send JSON data instead of form data
  const response = await api.post<AuthResponse>('/auth/token', credentials);
  
  // Store token in localStorage
  localStorage.setItem('token', response.data.access_token);
  
  return response.data;
};

export const register = async (userData: UserCreate): Promise<User> => {
  const response = await api.post<User>('/users', userData);
  return response.data;
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await api.get<User>('/users/me');
  return response.data;
};

// Note API functions
interface NotesResponse {
  items: Note[];
  total: number;
}

export const getTotalNotes = async (filter: Omit<NoteFilter, 'skip' | 'limit'> = {}): Promise<number> => {
  const params: Record<string, string> = {};
  
  if (filter.search) params.search = filter.search;
  if (filter.start_date) params.start_date = filter.start_date;
  if (filter.end_date) params.end_date = filter.end_date;
  
  const response = await api.get<{ total: number }>('/notes/total', { params });
  return response.data.total;
};

export const getNotes = async (filter: Partial<NoteFilter> = {}): Promise<{ items: Note[]; total: number }> => {
  try {
    const params: Record<string, string> = {};
    
    if (filter.search) params.search = filter.search;
    if (filter.start_date) params.start_date = filter.start_date;
    if (filter.end_date) params.end_date = filter.end_date;
    if (filter.category_id) params.category_id = filter.category_id.toString();
    if (filter.order_by) params.order_by = filter.order_by;
    if (filter.order) params.order = filter.order;
    if (filter.skip !== undefined) params.skip = filter.skip.toString();
    if (filter.limit !== undefined) params.limit = filter.limit.toString();

    const response = await api.get<{ items: Note[]; total: number }>(`/notes?${new URLSearchParams(params).toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getNote = async (id: number): Promise<Note> => {
  const response = await api.get<Note>(`/notes/${id}`);
  return response.data;
};

export const createNote = async (note: NoteCreate): Promise<Note> => {
  const response = await api.post<Note>('/notes', note);
  return response.data;
};

export const updateNote = async (id: number, note: NoteUpdate): Promise<Note> => {
  const response = await api.put<Note>(`/notes/${id}`, note);
  return response.data;
};

export const deleteNote = async (id: number): Promise<void> => {
  await api.delete(`/notes/${id}`);
};

export const addAttachment = async (
  noteId: number,
  file: File,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<Attachment> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<Attachment>(`/notes/${noteId}/attachments`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
  return response.data;
};

export const getAttachments = async (noteId: number): Promise<Attachment[]> => {
  const response = await api.get<Attachment[]>(`/notes/${noteId}/attachments`);
  return response.data;
};

export const deleteAttachment = async (attachmentId: number): Promise<void> => {
  await api.delete(`/attachments/${attachmentId}`);
};

// Category API functions
export const getCategories = async (): Promise<Category[]> => {
  const response = await api.get<Category[]>('/categories');
  return response.data;
};

export const createCategory = async (name: string, parent_id?: number): Promise<Category> => {
  const response = await api.post<Category>('/categories', { name, parent_id });
  return response.data;
};

export const updateCategory = async (id: number, name: string, parent_id?: number): Promise<Category> => {
  try {
    console.log('API Request:', { id, name, parent_id });
    const response = await api.put<Category>(`/categories/${id}`, { name, parent_id });
    console.log('API Response:', response.data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
    }
    throw error;
  }
};

export const deleteCategory = async (id: number): Promise<void> => {
  await api.delete(`/categories/${id}`);
};
